"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import ThemeToggle from './ThemeToggle';
import { cn } from '@/lib/utils';

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface HeaderProps {
  breadcrumbs?: BreadcrumbItem[];
}

const Header = ({ breadcrumbs }: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const pathname = usePathname();
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          } else if (activeSection === entry.target.id) {
            const rect = entry.target.getBoundingClientRect();
            if (rect.top > window.innerHeight / 2) {
              setActiveSection('');
            }
          }
        });
      },
      {
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0
      }
    );

    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, [activeSection]);

  const navLinks = [
    { href: '/about', label: 'About', isPage: true },
    { 
      href: pathname === '/' ? '#contact' : '/#contact', 
      label: 'Contact',
      isPage: pathname !== '/'
    }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header 
      className={cn(
        "fixed top-0 w-full z-50 transition-all duration-300 px-6 md:px-10",
        scrolled ? "py-4 bg-background/80 backdrop-blur-md border-b" : "py-6"
      )}
    >
      <div className="container max-w-6xl mx-auto flex items-center justify-between">
        <Link 
          href="/" 
          className="text-xl font-medium tracking-tight hover:text-accent transition-colors"
          onClick={scrollToTop}
        >
          AR
        </Link>
        
        <div className="hidden md:flex items-center space-x-8">
          {breadcrumbs ? (
            <nav className="flex items-center space-x-2 text-sm">
              {breadcrumbs.map((item, index) => (
                <div key={index} className="flex items-center">
                  {index > 0 && <span className="mx-2 text-muted-foreground">/</span>}
                  <Link 
                    href={item.href} 
                    className={cn(
                      "hover:text-accent transition-colors",
                      index === breadcrumbs.length - 1 
                        ? "text-accent font-medium underline underline-offset-4" 
                        : "text-muted-foreground"
                    )}
                  >
                    {item.label}
                  </Link>
                </div>
              ))}
            </nav>
          ) : (
            <nav className="flex items-center space-x-8 text-sm">
              {navLinks.map((link) => (
                link.isPage ? (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="relative transition-colors hover:text-accent"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className={cn(
                      "relative transition-colors hover:text-accent",
                      "after:absolute after:bottom-[-4px] after:left-0 after:h-0.5 after:w-0 after:bg-accent after:transition-all after:duration-300",
                      activeSection === link.href.slice(1) && "text-accent after:w-full"
                    )}
                  >
                    {link.label}
                  </a>
                )
              ))}
            </nav>
          )}
          <ThemeToggle />
        </div>
        
        <div className="md:hidden flex items-center space-x-4">
          <ThemeToggle />
          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex flex-col justify-center items-center w-8 h-8 space-y-1.5"
            aria-label="Toggle menu"
          >
            <span 
              className={cn(
                "w-6 h-0.5 bg-foreground transition-all", 
                menuOpen && "transform rotate-45 translate-y-2"
              )}
            />
            <span 
              className={cn(
                "w-6 h-0.5 bg-foreground transition-all", 
                menuOpen && "opacity-0"
              )}
            />
            <span 
              className={cn(
                "w-6 h-0.5 bg-foreground transition-all", 
                menuOpen && "transform -rotate-45 -translate-y-2"
              )}
            />
          </button>
        </div>
      </div>
      
      {/* Mobile menu */}
      <div 
        className={cn(
          "fixed inset-0 top-[72px] bg-background z-40 md:hidden transition-transform duration-300",
          menuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <nav className="flex flex-col space-y-8 p-8 text-lg">
          {breadcrumbs ? (
            breadcrumbs.map((item, index) => (
              <Link
                key={index}
                href={item.href}
                className={cn(
                  "hover:text-accent transition-colors",
                  index === breadcrumbs.length - 1 
                    ? "text-accent font-medium underline underline-offset-4" 
                    : ""
                )}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))
          ) : (
            navLinks.map((link) => (
              link.isPage ? (
                <Link
                  key={link.href}
                  href={link.href}
                  className="relative transition-colors hover:text-accent"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className={cn(
                    "relative transition-colors hover:text-accent",
                    "after:absolute after:bottom-[-4px] after:left-0 after:h-0.5 after:w-0 after:bg-accent after:transition-all after:duration-300",
                    activeSection === link.href.slice(1) && "text-accent after:w-full"
                  )}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              )
            ))
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
